import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { AppResource } from 'src/app/app.resource';
import { Civilite } from 'src/app/core/models/form-state.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Question } from 'src/app/core/models/question.model';
import { Step } from 'src/app/core/models/step.model';
import { State } from 'src/app/core/state/core.state';
import { Store } from '@ngrx/store';
import { String } from 'typescript-string-operations';
import { finalize, lastValueFrom, take } from 'rxjs';
import * as fromContext from '../../../core/state/context';
import * as fromPath from '../../../core/state/path';
import * as fromSubscriber from '../../../core/state/subscriber';
import * as fromAddress from '../../../core/state/address';
import * as fromHousing from '../../../core/state/housing';
import * as fromOffer from '../../../core/state/offer-customization';
import * as fromTarification from '../../../core/state/tarification';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { Info } from 'src/app/core/models/info.model';
import { TarifService } from 'src/app/api/services/tarif.service';
import { ConsentService } from 'src/app/api/services/consent.service';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { ApiRequestBodyUtils } from 'src/app/shared/utils/apiRequestBodyUtils';
import { ApiResponseBodyUtils } from 'src/app/shared/utils/apiResponseBodyUtils';
import { MatomoTracker } from 'ngx-matomo-client';
import { DateUtils } from 'src/app/shared/utils/dateUtils';
import { ReferenceDataService } from 'src/app/shared/services/reference-data-service';
import * as fromInfo from '../../../core/state/info';
import { Stepper } from 'src/app/core/models/stepper.model';
import { VyvCountryCodeModel } from 'src/app/api/models/vyvCountryCodeModel';
import { CountriesService } from 'src/app/api/services/countries.service';
import { AppConfig } from 'src/app/app.config';
import { SubscriberCGVm } from 'src/app/shared/models/components/subscriberCGVm';
import { BackendContextSouscripteur } from 'src/app/core/models/context.model';

interface Civility {
  value: Civilite;
  text: string;
}

@Component({
  selector: 'app-subscriber',
  templateUrl: './subscriber.component.html',
  styleUrls: ['./subscriber.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SubscriberComponent extends BaseComponent implements OnInit {
  override step: Step = {
    step: this.resource.header.stepContact,
    stepNumber: 2,
    subStep: this.resource.header.subStepSubscriber,
    subStepNumber: 0
  };

  override question: Question = {
    libelle: this.resource.question.subscriber,
    popupInfo: undefined
  };

  override info: Info = {
    title: this.resource.infotop.beforeLastStep,
    secondTitle: '',
    subTitle: this.resource.infotop.beforeLastStepB,
    logo: this.resource.infotop.logoTime,
    isVisible: true
  };

  maxDate: Date;

  subscriberForm!: FormGroup;
  stateDataContextSubscriber: BackendContextSouscripteur | null | undefined;

  civilities!: Civility[];
  selectedCivility: Civility | undefined;
  path!: PathType;
  subscriberCGVm!: SubscriberCGVm;

  estClient: boolean = false;

  countries = new Array<VyvCountryCodeModel>();
  filteredCountries = new Array<VyvCountryCodeModel>();
  context!: string;

  get civility() {
    return this.subscriberForm.get('civility');
  }

  get firstName() {
    return this.subscriberForm.get('firstName');
  }

  get lastName() {
    return this.subscriberForm.get('lastName');
  }

  get birthday() {
    return this.subscriberForm.get('birthday');
  }

  get birthdayPlace() {
    return this.subscriberForm.get('birthdayPlace');
  }

  get birthdayCountry() {
    return this.subscriberForm.get('birthdayCountry');
  }

  get email() {
    return this.subscriberForm.get('email');
  }

  get confirmEmail() {
    return this.subscriberForm.get('confirmEmail');
  }

  get phone() {
    return this.subscriberForm.get('phone');
  }

  get brand() {
    return this.appConfig.getConfig('brand');
  }

  isLoading: boolean = false;

  constructor(
    private router: Router,
    store: Store<State>,
    resources: AppResource,
    tracker: MatomoTracker,
    private tarifService: TarifService,
    private consentService: ConsentService,
    private countriesService: CountriesService,
    @Inject(ReferenceDataService) private refDataService: ReferenceDataService,
    @Inject(AppConfig) private appConfig: AppConfig
  ) {
    super(store, resources, tracker);

    let today = new Date();
    today = new Date(today.setFullYear(today.getFullYear() - 18));
    this.maxDate = new Date(today.setDate(today.getDate() - 1));
    this.civilities = [
      { text: Civilite.Monsieur, value: Civilite.Monsieur },
      { text: Civilite.Madame, value: Civilite.Madame },
      { text: Civilite.Autre, value: Civilite.Autre }
    ];
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    this.subscriberCGVm = new SubscriberCGVm(
      String.format(
        this.resource.subscriber.text1CG,
        this.path === PathType.HOUSING_PARIS
          ? 'Assurance Habitation Parisienne'
          : 'Assurance Habitation Confort'
      ),
      this.resource.subscriber.text2CG,
      this.resource.subscriber.text3CG,
      this.resource.subscriber.text3bisCG,
      this.resource.subscriber.text4CG,
      this.resource.subscriber.text5CG
    );

    this.updateResumeStepVisibility(true);
    await this.loadCountries();

    this.initForm();
    await this.loadStateData();

    // Désactiver la modification du formulaire dans le cas d'un client (non prospect) si la valeur est connue
    if (this.estClient) {
      this.disableExistingContextDataFormControls();
    }
  }

  async loadCountries() {
    this.countries = await lastValueFrom(
      this.countriesService.GetCountries(undefined, 'fr').pipe(take(1))
    );
    this.filteredCountries = this.countries;
  }

  private initForm() {
    this.subscriberForm = new FormGroup(
      {
        civility: new FormControl<string | null>(null, {
          validators: [Validators.required],
          nonNullable: true
        }),
        firstName: new FormControl<string | null>(null, {
          validators: [Validators.required, Validators.maxLength(50)],
          nonNullable: true
        }),
        lastName: new FormControl<string | null>('', {
          validators: [Validators.required, Validators.maxLength(50)],
          nonNullable: true
        }),
        birthday: new FormControl<string | null>(null, {
          validators: [Validators.required],
          nonNullable: true
        }),
        birthdayPlace: new FormControl<string | null>('', {
          validators: [Validators.required],
          nonNullable: true
        }),
        birthdayCountry: new FormControl<VyvCountryCodeModel | null>(
          this.countries.find(x => x.CountryNameFR == 'France')!,
          {
            validators: [Validators.required],
            nonNullable: true
          }
        ),
        email: new FormControl<string | null>('', {
          validators: [
            Validators.required,
            Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')
          ],
          nonNullable: true
        }),
        confirmEmail: new FormControl<string | null>('', {
          validators: [
            Validators.required,
            Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')
          ],
          nonNullable: true
        }),
        phone: new FormControl<string | null>('', {
          validators: [
            Validators.pattern(
              /^(?:(?:(?:06|07)[ ]?(?:\(0\)[ ]?)?)|0){1}[6-7]{1}([ .-]?)(?:\d{2}\1?){3}\d{2}$/g
            )
          ]
        }),
        consentValid: new FormControl<boolean | undefined>(undefined, {
          validators: this.brand === 'HM' ? [Validators.required] : [],
          nonNullable: false
        })
      },
      {
        updateOn: 'change',
        validators: [emailMatchValidator('email', 'confirmEmail')]
      }
    );
  }
  public validCheckboxes($event: any) {
    if (!$event.voies.some((c: any) => c.isCheck === undefined)) {
      this.subscriberForm.controls['consentValid'].setValue(true);
    }
  }

  disableExistingContextDataFormControls() {
    if (this.stateDataContextSubscriber?.civilite)
      this.subscriberForm.controls['civility'].disable();
    if (this.stateDataContextSubscriber?.prenom)
      this.subscriberForm.controls['firstName'].disable();
    if (this.stateDataContextSubscriber?.nom) this.subscriberForm.controls['lastName'].disable();
    if (
      this.stateDataContextSubscriber?.paysNaissance &&
      this.countries.find(
        c =>
          c[this.brand === 'HM' ? 'CountryCode' : 'CountryNameFR'] ===
          this.stateDataContextSubscriber?.paysNaissance
      )
    ) {
      this.subscriberForm.controls['birthdayCountry'].disable();
    }
    if (this.stateDataContextSubscriber?.dateNaissance)
      this.subscriberForm.controls['birthday'].disable();
    if (this.stateDataContextSubscriber?.villeNaissance)
      this.subscriberForm.controls['birthdayPlace'].disable();
    if (this.stateDataContextSubscriber?.email) {
      this.subscriberForm.controls['email'].disable();
      this.subscriberForm.controls['confirmEmail'].disable();
    }
  }

  private async loadStateData() {
    this.store.dispatch(
      fromSubscriber.changeValidationStatus({
        isValid: false
      })
    );

    let contextId = await lastValueFrom(
      this.store.select(fromContext.selectContextId).pipe(take(1))
    );
    this.context = contextId!;

    var stateContextData = await lastValueFrom(
      this.store.select(fromContext.selectContextData).pipe(take(1))
    );

    this.stateDataContextSubscriber = stateContextData?.Souscripteur;

    var res = await lastValueFrom(
      this.store.select(fromSubscriber.selectSubscriber).pipe(take(1))
    )!;

    this.estClient = res.subscriber?.EstClient || false;
    this.selectedCivility = this.civilities.find(obj => {
      return obj.value === res.subscriber?.Civilite;
    });
    this.lastName?.setValue(res.subscriber?.Nom);
    this.firstName?.setValue(res.subscriber?.Prenom);
    this.birthday?.setValue(DateUtils.IsoToDayMonthYear(res.subscriber?.DateNaissance));
    this.birthdayPlace?.setValue(res.subscriber?.LieuNaissance);
    this.birthdayCountry?.setValue(
      this.brand === 'HM'
        ? this.countries.find(x => x.CountryCode === res.subscriber?.PaysNaissance)!
        : this.countries.find(x => x.CountryNameFR === res.subscriber?.PaysNaissance)!
    );
    this.email?.setValue(res.subscriber?.Email);
    if (this.estClient && res.subscriber?.Email) this.confirmEmail?.setValue(res.subscriber?.Email);
    this.phone?.setValue(res.subscriber?.Telephone);
    if (this.estClient) {
      this.subscriberForm.controls['consentValid'].clearValidators();
    }
  }

  async filterCountries($event: any) {
    let query = $event.query;
    if (query != null) {
      var trimmedQuery = query
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr-FR');
      this.filteredCountries = this.countries.filter(item => {
        return item.CountryNameFR.normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('fr-FR')
          .includes(trimmedQuery);
      });
    } else {
      this.filteredCountries = this.countries;
    }
  }

  async confirmStep() {
    this.isLoading = true;
    this.store.dispatch(
      fromSubscriber.patchSubscriber({
        payload: {
          Civilite: this.selectedCivility?.value,
          Nom: this.lastName?.value,
          Prenom: this.firstName?.value,
          Email: this.email?.value.toLowerCase(),
          Telephone: this.phone?.value,
          DateNaissance: DateUtils.DayMonthYearToIso(this.birthday?.value) ?? undefined,
          LieuNaissance: this.birthdayPlace?.value,
          PaysNaissance: this.birthdayCountry?.value.CountryNameFR
        }
      })
    );

    // Retrieve context
    const context = await lastValueFrom(
      this.store.select(fromContext.selectContextId).pipe(take(1))
    );

    // Retrieve product
    const product = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));

    // Retrieve address
    const stateAddress = await lastValueFrom(
      this.store.select(fromAddress.selectAddress).pipe(take(1))
    );

    // Retrieve housing informations
    const stateHousing = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );

    // Retrieve selected formule informations
    const stateOffer = await lastValueFrom(
      this.store.select(fromOffer.selectOfferCustomization).pipe(take(1))
    );

    const body = ApiRequestBodyUtils.GenerateTarifCreateRequestBody(
      context!,
      product,
      this.brand,
      stateAddress.adresseType,
      stateAddress.addresseSelection,
      stateAddress.addresseManuelle,
      stateAddress.adresseEsh,
      stateHousing.SaisieHabitation,
      {
        ...stateOffer.SaisieFormule,
        FormuleSelectionnee: undefined // Recalculate all formules
      },
      stateOffer.FormuleRecommandee!
    );

    let consent = (
      await lastValueFrom(this.store.select(fromSubscriber.selectSubscriber).pipe(take(1)))
    ).subscriber?.Consentement;

    this.tarifService
      .createTarif(body)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: res => {
          this.store.dispatch(
            fromSubscriber.changeValidationStatus({
              isValid: true
            })
          );

          // Store returned informations about formules in the state
          const tarifInfos = ApiResponseBodyUtils.ExtractTarifResponseBody(res);
          this.store.dispatch(
            fromTarification.patchTarification({
              payload: tarifInfos
            })
          );

          // Send consent informations for HM case
          if (consent && this.brand === 'HM') {
            const bodyConsent = ApiRequestBodyUtils.GeneratePostConsentRequestBody(
              this.context,
              consent.IdLibelle,
              consent.VoiesConsentement
            );
            this.consentService.PostConsentement(bodyConsent).subscribe({
              error: error => {
                console.error("Une erreur s'est produite :", error);
              },
              complete: () => {
                this.router.navigate(['/rate']);
              }
            });
          } else {
            this.router.navigate(['/rate']);
          }
        },
        error: error => {
          console.error("Une erreur s'est produite :", error);
        }
      });

    await this.updateStepper();
  }

  async updateStepper() {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    var tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.offer
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.subscriber
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.rate
    );

    if (subtitleN) subtitleN.isActive = true;

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  ToLowerCase(event: any) {
    let value = event.target.value;
    event.target.value = value.toLowerCase();
  }

  ToCapitalize(event: any) {
    let value = event.target.value;
    if (value[0] != undefined) event.target.value = value[0].toUpperCase() + value.slice(1);
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }
}

export function emailMatchValidator(field1: string, field2: string) {
  return function (frm: any) {
    let field1Value = frm.get(field1).value;
    let field2Value = frm.get(field2).value;

    if (frm != null) {
      if (field1Value != null && field2Value != null) {
        if (field1Value.toLowerCase() != field2Value.toLowerCase()) {
          return { matchEmail: {} };
        } else {
          return null;
        }
      }
    }

    return null;
  };
}
